import { getGTMData } from "@helper/getMetaData";
import { useNewRelic } from "@hooks/useNewRelic";
import { USFOrgSchema } from "@sphtech/web2-seo-schema";
import type { GADataProps } from "@typings/GA";
import { useEffect } from "react";
import {
  Links,
  Meta,
  Scripts,
  ScrollRestoration,
  useMatches,
} from "react-router";

type OauthLayoutProps = {
  children: React.ReactNode;
};

export function BaseLayout({ children }: { children: React.ReactNode }) {
  const matches = useMatches();
  useNewRelic();

  const gaData = matches.map((match) => {
    const loaderData = match.loaderData;
    const payload =
      loaderData && typeof loaderData === "object" && "payload" in loaderData
        ? (loaderData as { payload: unknown }).payload
        : undefined;

    const loaderGa =
      payload && typeof payload === "object" && "gaData" in payload
        ? (payload as { gaData: GADataProps }).gaData
        : undefined;
    return loaderGa;
  });
  const validGaData = gaData.find(Boolean) || ({} as GADataProps); // Use the first valid gaData or an empty object if none found
  const gtmData = getGTMData(validGaData);
  return (
    <html lang="ms">
      <head>
        <meta charSet="UTF-8" />
        <link href="/favicon.ico" rel="icon" type="image/x-icon" />
        <meta content="width" name="MobileOptimized" />
        <meta content="true" name="HandheldFriendly" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <meta content="ie=edge" httpEquiv="x-ua-compatible" />

        {/* Inject GTM data server-side */}
        <script
          id="ga_data_layer"
          suppressHydrationWarning // Add this to prevent hydration warnings from cookie logic
          dangerouslySetInnerHTML={{
            __html: `
            // Initialize GTM data layer from route handles
            var _data = ${JSON.stringify(gtmData)};

            _data.permutive_id = window.localStorage.getItem("permutive-id") || "";
            window.addEventListener('load', function() {
              if (typeof window.getCookieByName === 'function') {
                if(window.getCookieByName("visitorcat") != undefined && window.getCookieByName("mysphw") != undefined) {
                  var visitorcat = window.getCookieByName("visitorcat");
                  _data.visitorcat = visitorcat;
                  _data.at = window.getCookieByName("mysphw");
                }
                if(window.getCookieByName("svc") != undefined) {
                  var svc = window.getCookieByName("svc");
                  _data.svc = svc;
                }
              }
            });`,
          }}
        />
        <script suppressHydrationWarning>{`var gs_channels = [];`}</script>
        <Meta />
        <Links />
        <LightboxScript />
      </head>
      <body suppressHydrationWarning>
        <USFOrgSchema
          name="Berita Harian"
          description="Berita Harian ialah akhbar harian berbahasa Melayu yang diterbitkan di Singapura oleh SPH Media."
          url="https://www.beritaharian.sg/"
          logo={{
            url: "https://www.beritaharian.sg/web2-assets/BHLogoStandard-Cv1AdY0g.svg",
            width: 600,
            height: 60,
          }}
          sameAs={[
            "https://www.facebook.com/BeritaHarianSG/",
            "https://x.com/BeritaHarianSG/",
            "https://www.instagram.com/BeritaHarianSG/",
            "https://www.youtube.com/user/BeritaHarianSG",
          ]}
          memberOf={{
            "@type": "ProgramMembership",
            hostingOrganization: {
              "@type": "Organization",
              name: "SPH Media Ltd",
              url: "https://www.sph.com.sg/",
              logo: {
                "@type": "ImageObject",
                url: "https://static.sph.com.sg/uploads/2024/03/SPH-Media-Logo-New.webp",
                width: 100,
                height: 100,
              },
            },
          }}
          contactPoint={{
            telephone: "+65 6388 3838",
            contactType: "customer support",
            availableLanguage: "Malay",
            areaServed: {
              "@type": "Country",
              name: "Singapore",
            },
          }}
        />
        {children}
        <div id="mySPHLightbox" suppressHydrationWarning />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function OauthLayout({ children }: OauthLayoutProps) {
  return (
    <html lang="en">
      <head>
        <meta charSet="UTF-8" />
        <link href="/favicon.ico" rel="icon" type="image/x-icon" />
        <Meta />
        <Links />
        <LightboxScript />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function LightboxScript(): React.ReactElement {
  // Coerce raw env values to string (or undefined) to avoid assigning `any` to JSX props
  const lightboxSrc = import.meta.env.VITE_PUBLIC_MYSPH_LIGHTBOX2_JS
    ? String(import.meta.env.VITE_PUBLIC_MYSPH_LIGHTBOX2_JS)
    : undefined;
  const lightboxClientId = import.meta.env.VITE_PUBLIC_MYSPH_LIGHTBOX2_CLIENTID
    ? String(import.meta.env.VITE_PUBLIC_MYSPH_LIGHTBOX2_CLIENTID)
    : undefined;

  useEffect(() => {
    if (lightboxSrc) {
      const script = document.createElement("script");
      script.src = lightboxSrc;
      script.type = "module";
      script.async = true;
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    }
  }, [lightboxSrc]);

  return (
    <script
      suppressHydrationWarning
      dangerouslySetInnerHTML={{
        __html: `
              window.mySPHConfig = {
                clientId: "${lightboxClientId}",
                pubId: "bh",
                language: "ms",
                providers: ["google", "facebook", "googleOneTap", "apple"],
                ssoMode: "manual",
              };

              // Polyfill window.mySPHLightbox with a proxy-like setter to ensure methods always exist
              (function() {
                var _val = window.mySPHLightbox || {};

                function ensureMethods(obj) {
                  obj = obj || {};
                  if (typeof obj.isAuthenticated !== 'function') {
                    obj.isAuthenticated = function() { return Promise.resolve(false); };
                  }
                  if (typeof obj.getUserInfo !== 'function') {
                    obj.getUserInfo = function() { return Promise.resolve(null); };
                  }
                  if (typeof obj.initiateLogin !== 'function') {
                    obj.initiateLogin = function() {};
                  }
                  if (typeof obj.showProfile !== 'function') {
                    obj.showProfile = function() {};
                  }
                  return obj;
                }

                _val = ensureMethods(_val);

                try {
                  Object.defineProperty(window, 'mySPHLightbox', {
                    configurable: true,
                    enumerable: true,
                    get: function() {
                      return _val;
                    },
                    set: function(newVal) {
                      _val = ensureMethods(newVal);
                    }
                  });
                } catch (e) {
                  console.error("Failed to define property mySPHLightbox", e);
                  // Fallback if defineProperty fails
                  window.mySPHLightbox = _val;
                }
              })();

              if (!window.mySPHConfig.clientId || window.mySPHConfig.clientId === "undefined") {
                console.error("VITE_PUBLIC_MYSPH_LIGHTBOX2_CLIENTID is not defined. Please check your environment variables.");
              }
            `,
      }}
    />
  );
}
